import axios from 'axios';
import { authService } from '@/services/auth';
import errorTracker from './errorTracker';

const defaultOptions = {
  baseURL: process.env.VUE_APP_API_URL,
  crossDomain: true,
  headers: {},

  // request interceptor handler
  reqHandleFunc: async config => {
    const token = await authService.getToken();

    if (!token) {
      throw new axios.Cancel('Bad token');
    }

    config.headers.common.Authorization = `Bearer ${token}`;

    return config;
  },
  reqErrorFunc: async error => {
    throw error;
  },

  // response interceptor handler
  resHandleFunc: response => response,
  resErrorFunc: error => {
    if (error.message === 'Bad token') {
      console.error('Bad token');
      authService.reset();
      authService.login();
      return Promise.reject(error);
    }

    if (!error.response) {
      console.error('Network error. Check your connection.');
      return Promise.reject(error);
    }

    const authHeader = error.response.headers?.['www-authenticate'];
    if (authHeader === 'Bearer error="invalid_token"') {
      console.error('Bad token provided', error);
      authService.login();
      return;
    }

    errorTracker.trackException({ exception: error });

    return Promise.reject(error);
  },
};

const axiosInstance = axios.create(defaultOptions);

axiosInstance.interceptors.request.use(
  async config => defaultOptions.reqHandleFunc(config),
  async error => defaultOptions.reqErrorFunc(error)
);

axiosInstance.interceptors.response.use(
  response => defaultOptions.resHandleFunc(response),
  error => defaultOptions.resErrorFunc(error)
);

export default axiosInstance;

const axiosAnonymous = axios.create(defaultOptions);

const fallbackStatusCodes = {
  // 2xx Success
  Ok: 200,
  Created: 201,
  Accepted: 202,
  NoContent: 204,

  // 3xx Redirection
  MovedPermanently: 301,
  Found: 302,
  NotModified: 304,
  TemporaryRedirect: 307,
  PermanentRedirect: 308,

  // 4xx Client Errors
  BadRequest: 400,
  Unauthorized: 401,
  Forbidden: 403,
  NotFound: 404,
  MethodNotAllowed: 405,
  Conflict: 409,
  UnprocessableEntity: 422,
  TooManyRequests: 429,

  // 5xx Server Errors
  InternalServerError: 500,
  BadGateway: 502,
  ServiceUnavailable: 503,
  GatewayTimeout: 504,
};

// Re-export HTTP status codes for convenience
// If axios doesn't have HttpStatusCode (versions < 1.1.3), use fallback
export const HttpStatusCode = axios.HttpStatusCode ?? fallbackStatusCodes;

if (axios.HttpStatusCode) {
  // Axios has native HttpStatusCode enum (v1.1.3+)
  throw new Error(
    '⚠️ CLEANUP REQUIRED: Axios now includes native HttpStatusCode enum. ' +
      'Please remove the fallback HttpStatusCode object from src/plugins/axios.js ' +
      'and replace with: export const HttpStatusCode = axios.HttpStatusCode;'
  );
}

export { axiosInstance, axiosAnonymous };
